import { CheckboxAllOptionsInput } from '@framework/form/inputs/CheckboxAllOptionsInput.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { type ColumnType } from 'antd/es/table';
import { keys, pickBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectSearchInput } from '../advanced-select-common/components/AdvancedSelectSearchInput.tsx';
import { AdvancedSelectTableToolbar } from '../advanced-select-common/components/AdvancedSelectTableToolbar.tsx';
import { SelectionCounter } from '../advanced-select-common/components/SelectionCounter.tsx';
import { getAdvancedTableDataColumns } from '../advanced-select-common/form/getAdvancedTableDataColumns.ts';
import { getAdvancedSelectColumnRenderByDatatype } from '../advanced-select-common/form/getRenderByDatatype.tsx';
import { useAdvancedSelectSearch } from '../advanced-select-common/hooks/useAdvancedSelectSearch.ts';
import { useShowOnlySelectedFilter } from '../advanced-select-common/hooks/useShowOnlySelectedFilter.ts';
import { type IRuntimeWidgetAdvancedOption } from '../advanced-select-common/IAdvancedSelectWidget.ts';
import {
  ADVANCED_SELECT_TABLE_SCROLLABLE_HEIGHT,
  sortColumnValues,
} from '../advanced-select-common/table/tableUtils.ts';
import { getWidgetProps } from '../common/util/getWidgetProps.ts';
import { MultiSelectOption } from '../multi-select/components/MultiSelectOption.tsx';
import { multiSelectOptionsObjectName } from '../multi-select/getMultiSelectOptionsObjectName.ts';
import { type IAdvancedMultiSelectWidget } from './IAdvancedMultiSelectWidget.ts';
import { useAdvancedMultiSelectWidgetOptions } from './useAdvancedMultiSelectWidgetOptions.tsx';

const CHECK_COLUMN_WIDTH = 32;

const CheckColumnContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CHECK_COLUMN_WIDTH}px;

  .ant-checkbox-wrapper {
    margin-inline-end: 0;
  }

  .hide-label.ant-checkbox-wrapper > span:last-child {
    padding-inline-start: 0;
    padding-inline-end: 0;
  }
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  padding-inline: 16px;
  padding-top: 8px;
`;

export interface IAdvancedMultiSelectTableDataProps
  extends IWidgetComponentProps<IAdvancedMultiSelectWidget> {
  /** Mappa id-opzione -> selezionata, per evidenziare le righe già scelte. */
  selectedOptions?: Record<string, boolean> | null;
  /** Se true mostra solo le opzioni selezionate. */
  showOnlySelected: boolean;
}

/**
 * Tabella delle opzioni per la selezione multipla avanzata: ricerca, ordinamento,
 * paginazione e selezione tramite checkbox. Renderizza ogni colonna in base al
 * proprio tipo (incluso RichText come HTML sanificato).
 */
export function AdvancedMultiSelectTableData(
  props: IAdvancedMultiSelectTableDataProps,
) {
  const { widget, selectedOptions, showOnlySelected } = props;
  const widgetOptions = getWidgetProps(props);
  const isMobile = useBreakpoint(down('md'));
  const { prefix } = useWidgetScopeContext();

  const groupName = useMemo(
    () => `${prefix}${widget.code}`,
    [prefix, widget.code],
  );

  const dataSource = useAdvancedMultiSelectWidgetOptions({
    widget,
    name: groupName,
    preview: widgetOptions.preview,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const selectedOptionIds = useMemo(() => {
    if (!selectedOptions) return [];
    return keys(pickBy(selectedOptions, value => value === true));
  }, [selectedOptions]);

  const rawColumns = useMemo(
    () => getAdvancedTableDataColumns(widget),
    [widget],
  );
  const { setSearchValue, filterData } = useAdvancedSelectSearch(rawColumns);

  const searchFilteredDataSource = useMemo(
    () => filterData(dataSource),
    [filterData, dataSource],
  );

  const { filteredDataSource } = useShowOnlySelectedFilter({
    dataSource: searchFilteredDataSource,
    selectedIds: selectedOptionIds,
    showOnlySelected,
    onToggle: () => setPagination(prev => ({ ...prev, current: 1 })),
  });

  const paginationConfig = useMemo(
    () => ({
      current: pagination.current,
      pageSize: pagination.pageSize,
      pageSizeOptions: [5, 10, 20, 50],
    }),
    [pagination.current, pagination.pageSize],
  );

  const handleTableChange = useCallback((paginationConfig: any) => {
    setPagination({
      current: paginationConfig.current ?? 1,
      pageSize: paginationConfig.pageSize ?? 10,
    });
  }, []);

  const columns = useMemo(() => {
    const checkColumn: ColumnType<IRuntimeWidgetAdvancedOption>[] = [
      {
        title: (
          <CheckColumnContent>
            <CheckboxAllOptionsInput
              groupName={groupName}
              optionsObjectName={multiSelectOptionsObjectName(groupName)}
              options={dataSource}
              selectedOptions={selectedOptions}
            />
          </CheckColumnContent>
        ),
        dataIndex: 'checkColumn',
        key: 'checkColumn',
        fixed: 'left' as const,
        align: 'center' as const,
        width: CHECK_COLUMN_WIDTH,
        render: (_: any, record: IRuntimeWidgetAdvancedOption) => {
          const adaptedOption = {
            ...record,
            value: record.id,
            label: [],
          };

          return (
            <CheckColumnContent>
              <MultiSelectOption
                widget={widget}
                groupName={groupName}
                name={groupName}
                option={adaptedOption}
                hideLabel
              />
            </CheckColumnContent>
          );
        },
      },
    ];

    return [
      ...checkColumn,
      ...(rawColumns.map(
        col =>
          ({
            title: col.label,
            dataIndex: ['values', col.id],
            key: col.id,
            // Non attiviamo sulle colonne lo scrooll automatico,
            // per evitare che un solo testo lungo faccia scrollare la tabella.
            // Lasciamo che la tabella cerchi di adattarsi alla pagina,
            // se troppo grande allora si attiva lo scroll sul container,
            // columnFlags: ['enableScroll'],
            render: getAdvancedSelectColumnRenderByDatatype(col.type),
            sorter: (
              a: IRuntimeWidgetAdvancedOption,
              b: IRuntimeWidgetAdvancedOption,
            ) =>
              sortColumnValues(
                a.values?.[col.id],
                b.values?.[col.id],
                col.type,
              ),
          }) as ColumnType<IRuntimeWidgetAdvancedOption>,
      ) ?? []),
    ];
  }, [widget, groupName, dataSource, selectedOptions, rawColumns]);

  return (
    <>
      <AdvancedSelectTableToolbar>
        <AdvancedSelectSearchInput
          onChange={value => {
            setSearchValue(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
        />
      </AdvancedSelectTableToolbar>
      <TableData
        removeShadow
        dataSource={filteredDataSource}
        scrollableHeight={
          isMobile ? undefined : ADVANCED_SELECT_TABLE_SCROLLABLE_HEIGHT
        }
        size="small"
        style={{ marginTop: '6px' }}
        pagination={paginationConfig}
        onChange={handleTableChange}
        columns={columns}
        darkTitleBar={false}
      />
      <TableFooter>
        <SelectionCounter
          selectedCount={selectedOptionIds.length}
          totalCount={searchFilteredDataSource.length}
        />
      </TableFooter>
    </>
  );
}
